import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle, MessageSquare, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: 'Product Inquiry — Request for Quote',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Your inquiry has been sent! We\'ll get back to you within 24 hours.');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <Card className="max-w-lg text-center" data-testid="inquiry-success-card">
          <CardContent className="p-12">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#0a1128] mb-4">Inquiry Received!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your interest. Our team will review your request and get back to you within 24 hours with a customized quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="browse-products-btn"
                onClick={() => navigate('/products')}
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128]"
              >
                Browse Products
              </Button>
              <Button
                data-testid="go-home-btn"
                variant="outline"
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="checkout-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30 mb-4 text-sm px-4 py-1">
            Personalized Service
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0a1128] mb-4">
            Request a Quote
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            We offer customized pricing tailored to your specific needs. Share your requirements and our team will craft a personalized quote for you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#D4AF37]" />
                  Your Inquiry
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="inquiry-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <Input
                        data-testid="inquiry-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <Input
                        data-testid="inquiry-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        required
                        className="h-12"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <Input
                      data-testid="inquiry-subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tell us about your needs *</label>
                    <textarea
                      data-testid="inquiry-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="What products are you interested in? Any specific quantities, event details, or customization requirements?"
                      rows={6}
                      required
                      className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a1128] focus:border-transparent resize-none"
                    />
                  </div>
                  <Button
                    data-testid="submit-inquiry-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128] font-semibold h-14"
                  >
                    {submitting ? 'Sending...' : 'Submit Inquiry'}
                    {!submitting && <ArrowRight className="ml-2 h-5 w-5" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar info */}
          <div className="space-y-6">
            <Card data-testid="why-inquire-card">
              <CardContent className="p-6">
                <h3 className="font-semibold text-[#0a1128] mb-4">Why Request a Quote?</h3>
                <div className="space-y-3">
                  {[
                    'Customized pricing based on your needs',
                    'Bulk order discounts available',
                    'Personalized product recommendations',
                    'Dedicated account manager',
                    'Flexible delivery options'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="contact-info-card">
              <CardContent className="p-6">
                <h3 className="font-semibold text-[#0a1128] mb-4">Reach Us Directly</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-sm text-gray-600">407-702-0465</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-sm text-gray-600">Smithhookahsupplyco@gmail.com</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
